/**
 * Tesseract.js OCR unit detection — ported from the Claude Design prototype.
 *
 * For raster/scanned plans (or flattened PDFs), Tesseract reads printed unit
 * labels off the image; matching tokens become rectangles centred on the label
 * just like the vector-PDF path.
 *
 * Preprocess: 3× upscale + binarize (dark ink → black, fills → white). Without
 * this Tesseract misses small labels printed over coloured store fills.
 */
import { createWorker, PSM } from 'tesseract.js';

const clamp = (v, a, b) => Math.max(a, Math.min(b, v));

function buildRegex(pattern) {
    try { return new RegExp(pattern, 'i'); }
    catch { return new RegExp(pattern); }
}

/**
 * Render an image URL to a binarized 3×-upscaled canvas suitable for Tesseract.
 * Uses crossOrigin='anonymous' so the source backend must allow CORS reads
 * (the API-EGoalV3 /uploads mount already sets the right headers).
 */
async function preprocessForOcr(imageUrl) {
    const img = await new Promise((resolve, reject) => {
        const im = new Image();
        im.crossOrigin = 'anonymous';
        im.onload = () => resolve(im);
        im.onerror = reject;
        im.src = imageUrl;
    });

    const scale = 3;
    const ow = Math.round((img.naturalWidth || img.width) * scale);
    const oh = Math.round((img.naturalHeight || img.height) * scale);
    const canvas = document.createElement('canvas');
    canvas.width = ow;
    canvas.height = oh;
    const ctx = canvas.getContext('2d');
    ctx.fillStyle = '#fff';
    ctx.fillRect(0, 0, ow, oh);
    ctx.imageSmoothingEnabled = true;
    ctx.imageSmoothingQuality = 'high';
    ctx.drawImage(img, 0, 0, ow, oh);

    const idata = ctx.getImageData(0, 0, ow, oh);
    const d = idata.data;
    for (let i = 0; i < d.length; i += 4) {
        const g = 0.299 * d[i] + 0.587 * d[i + 1] + 0.114 * d[i + 2];
        const v = g < 120 ? 0 : 255;
        d[i] = d[i + 1] = d[i + 2] = v;
    }
    ctx.putImageData(idata, 0, 0);
    return { canvas, ow, oh };
}

/**
 * Run OCR on a floor-plan image. Returns the recognized words (with normalized
 * bboxes) ready for `detectRectsFromOcr`.
 *
 * onProgress({ pct, status }) is called with Tesseract's logger output so the
 * caller can drive a progress bar (OCR takes 10-20s typical).
 */
export async function ocrWords(imageUrl, onProgress) {
    const { canvas, ow, oh } = await preprocessForOcr(imageUrl);
    let worker;
    try {
        worker = await createWorker({
            logger: (m) => {
                if (!onProgress) return;
                if (m.status === 'recognizing text') {
                    onProgress({ pct: m.progress, status: 'Reading labels…' });
                } else {
                    onProgress({ pct: m.progress || 0, status: 'Loading OCR engine…' });
                }
            },
        });
        await worker.loadLanguage('eng');
        await worker.initialize('eng');
        await worker.setParameters({ tessedit_pageseg_mode: PSM.SPARSE_TEXT });
        const res = await worker.recognize(canvas);

        return (res.data.words || []).map((w) => ({
            text: w.text,
            conf: w.confidence,
            nx: w.bbox.x0 / ow,
            ny: w.bbox.y0 / oh,
            nw: (w.bbox.x1 - w.bbox.x0) / ow,
            nh: (w.bbox.y1 - w.bbox.y0) / oh,
        }));
    } finally {
        if (worker) {
            try { await worker.terminate(); } catch {}
        }
    }
}

/**
 * OCR words → unit rectangles. Box is grown around each label so it covers a
 * realistic-sized unit rather than the tight text bbox.
 */
export function detectRectsFromOcr(words, pattern, minConf = 35) {
    const re = buildRegex(pattern);
    const seen = new Set();
    const out = [];
    let i = 0;
    for (const w of words) {
        if (w.conf < minConf) continue;
        const s = String(w.text || '').replace(/\s+/g, '');
        if (s.length < 2 || s.length > 14) continue;
        if (!/\d/.test(s)) continue;
        if (!re.test(s)) continue;
        const key = s.toLowerCase();
        if (seen.has(key)) continue;
        seen.add(key);

        const bw = Math.max(w.nw * 1.7, 0.045);
        const bh = Math.max(w.nh * 2.4, 0.05);
        const cx = w.nx + w.nw / 2;
        const cy = w.ny + w.nh / 2;
        out.push({
            id: 'o' + Date.now().toString(36) + (i++),
            code: s,
            detected: true,
            nx: clamp(cx - bw / 2, 0, 1 - bw),
            ny: clamp(cy - bh / 2, 0, 1 - bh),
            nw: bw,
            nh: bh,
        });
    }
    return out;
}
